import { useEffect, useRef, useState, useCallback, FormEvent, ClipboardEvent } from "react"
import SingleInput from "./SingleInput"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import { api } from "../../utils/apiHandler"
import { useTranslation } from "react-i18next"
import { useLocale } from "../../context/useLocale"
import useAuthResource from "../../resources/useAuthResource"
import i18n from "../../utils/i18n"
import Loader from "../ui/Loader"

/**
 * Props for the OTPInput component.
 * @property phoneInput - The phone number to which the OTP was sent.
 * @property setShowOtpScreen - Function to toggle the OTP screen visibility.
 */
interface OTPInputProps {
  phoneInput: string;
  setShowOtpScreen: (show: boolean) => void;
}

/**
 * Represents the values of each OTP input field.
 */
interface InputValues {
  input1: string;
  input2: string;
  input3: string;
  input4: string;
  input5: string;
  input6: string;
}

/**
 * OTPInput is a controlled component for entering a 6-digit OTP code.
 * Handles input, paste, resend, and submission logic.
 *
 * @component
 * @param {OTPInputProps} props - Component props
 * @returns {JSX.Element}
 */

/**
 * Helper function to create a locale object from a language code
 * This provides basic language labels for common language codes
 */
const createLocaleFromLanguageCode = (languageCode: string): { value: string; label: string } => {
  const languageMap: Record<string, string> = {
    en: "English",
    hi: "हिन्दी",
    ml: "മലയാളം",
    mr: "मराठी", 
    ar: "العربية",
    bn: "বাংলা",
    de: "Deutsch",
    es: "Español",
    fr: "Français",
    it: "Italiano",
    ja: "日本語",
    kn: "ಕನ್ನಡ",
    ko: "한국어",
    nl: "Nederlands",
    pt: "Português",
    ru: "Русский",
    ta: "தமிழ்",
    te: "తెలుగు",
    tr: "Türkçe",
    zh: "中文"
  };
  
  return {
    value: languageCode.toLowerCase(),
    label: languageMap[languageCode.toLowerCase()] || languageCode.toUpperCase()
  };
};

const OTPInput: React.FC<OTPInputProps> = ({ phoneInput, setShowOtpScreen }) => {
  const { t } = useTranslation();
  const { setSelectedLocale } = useLocale();
  const { verifyOtp } = useAuthResource();

  const [error, setError] = useState<string | null>(null);
  const [inputValues, setInputValues] = useState<InputValues>({
    input1: "",
    input2: "",
    input3: "",
    input4: "",
    input5: "",
    input6: "",
  });
  const [isOTPResent, setIsOTPResent] = useState(false);
  const [showResendIcon, setShowResendIcon] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Change input1Ref type for compatibility with SingleInput
  const input1Ref = useRef<HTMLInputElement>(null) as React.RefObject<HTMLInputElement>;
  const navigate = useNavigate();

  /**
   * Handles changes to individual OTP input fields.
   */
  // Change inputId type from keyof InputValues to string for compatibility
  const handleInputChange = useCallback((inputId: string, value: string) => {
    setError(null);
    setInputValues((prev) => ({
      ...prev,
      [inputId]: value,
    }));
  }, []);

  /**
   * Resets all OTP input fields and focuses the first input.
   */
  const handleRetry = useCallback(() => {
    setInputValues({
      input1: "",
      input2: "",
      input3: "",
      input4: "",
      input5: "",
      input6: "",
    });
    input1Ref.current?.focus();
  }, []);

  /**
   * Handles OTP form submission and verification.
   */
  const handleSubmit = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    const otp =
      inputValues.input1 +
      inputValues.input2 +
      inputValues.input3 +
      inputValues.input4 +
      inputValues.input5 +
      inputValues.input6;
    if (!otp || otp.length !== 6) {
      setError(t("invalidOTP"));
      return;
    }
    try {
      setIsLoading(true);
      const payload = {
        phone_number: phoneInput,
        verification_code: otp,
      };
      const res = await verifyOtp(payload);
      localStorage.setItem("handle", JSON.stringify(res));
      console.log("Setting locale based on user language code:", res.user.language_code, createLocaleFromLanguageCode(res.user.language_code));
      setSelectedLocale(createLocaleFromLanguageCode(res.user.language_code));
      i18n.changeLanguage(res.user.language_code);
      if (res.user.first_time_status) {
        navigate("/choose");
      } else {
        navigate("/sangati", { replace: true });
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
      handleRetry();
      setError(t("invalidOTP"));
    } finally {
      setIsLoading(false);
    }
  }, [inputValues.input1, inputValues.input2, inputValues.input3, inputValues.input4, inputValues.input5, inputValues.input6, t, phoneInput, verifyOtp, setSelectedLocale, navigate, handleRetry]);

  /**
   * Handles pasting a 6-digit OTP into the input fields.
   */
  const handlePaste = useCallback((ev: ClipboardEvent<HTMLInputElement>) => {
    const clip = ev.clipboardData.getData("text");
    if (!isNaN(Number(clip)) && Number.isInteger(parseInt(clip)) && clip.length >= 6) {
      const tempOTP: InputValues = {
        input1: clip[0] || "",
        input2: clip[1] || "",
        input3: clip[2] || "",
        input4: clip[3] || "",
        input5: clip[4] || "",
        input6: clip[5] || "",
      };
      setTimeout(() => {
        setInputValues(tempOTP);
      }, 500);
    }
  }, []);

  /**
   * Handles resending the OTP to the user's phone number.
   */
  const handleResend = useCallback(async () => {
    try {
      const payload = { phone_number: phoneInput };
      await api.post("auth/sign_in", payload);
      handleRetry();
      setIsOTPResent(true);
      toast.success(t("resendOTPToast"), {
        position: "bottom-center",
        hideProgressBar: true,
        icon: false,
        className: "right-3 top-3 ",
        style: {
          maxWidth: "90vw",
          borderRadius: "10px",
          backgroundColor: "#3B3C36",
          color: "#fff",
        },
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
    }
  }, [phoneInput, t, handleRetry]);

  // Show resend icon after 30 seconds
  useEffect(() => {
    setShowResendIcon(false);
    const timer = setTimeout(() => {
      setShowResendIcon(true);
    }, 30000);
    return () => clearTimeout(timer);
  }, [isOTPResent]);

  return (
    <div className="h-full flex flex-col w-full max-w-lg mx-auto px-2 md:px-6 lg:px-0">
      <button
        className="cursor-pointer mt-4 w-fit"
        onClick={() => setShowOtpScreen(false)}
        type="button"
        aria-label={t("back")}
      >
        <img src="/icons/backbtn.svg" alt="Back" />
      </button>
      <form onSubmit={handleSubmit} className="flex-1 flex flex-col mt-8 md:mt-12">
        <div>
          <h6 className="text-16 md:text-18 lg:text-20 mb-2 font-semibold">{t("OTPTitle")}</h6>
          <p className="mb-2 text-18 md:text-20 font-sharetech break-all">{phoneInput}</p>
          <div className="w-full mt-4 flex flex-wrap justify-center items-center gap-2 font-sharetech">
            {/* OTP Inputs */}
            <SingleInput
              id="input1"
              value={inputValues.input1}
              onValueChange={handleInputChange}
              previousId={null}
              nextId="input2"
              handlePaste={handlePaste}
              inputRef={input1Ref}
              error={error}
              isSettings={false}
              name="input1"
            />
            <SingleInput
              id="input2"
              value={inputValues.input2}
              onValueChange={handleInputChange}
              previousId="input1"
              nextId="input3"
              handlePaste={handlePaste}
              inputRef={undefined}
              error={error}
              isSettings={false}
              name="input2"
            />
            <SingleInput
              id="input3"
              value={inputValues.input3}
              onValueChange={handleInputChange}
              previousId="input2"
              nextId="input4"
              handlePaste={handlePaste}
              inputRef={undefined}
              error={error}
              isSettings={false}
              name="input3"
            />
            <SingleInput
              id="input4"
              value={inputValues.input4}
              onValueChange={handleInputChange}
              previousId="input3"
              nextId="input5"
              handlePaste={handlePaste}
              inputRef={undefined}
              error={error}
              isSettings={false}
              name="input4"
            />
            <SingleInput
              id="input5"
              value={inputValues.input5}
              onValueChange={handleInputChange}
              previousId="input4"
              nextId="input6"
              handlePaste={handlePaste}
              inputRef={undefined}
              error={error}
              isSettings={false}
              name="input5"
            />
            <SingleInput
              id="input6"
              value={inputValues.input6}
              onValueChange={handleInputChange}
              previousId="input5"
              nextId={null}
              handlePaste={handlePaste}
              inputRef={undefined}
              error={error}
              isSettings={false}
              name="input6"
            />
          </div>
          <p className="text-14 h-5 text-sangati-orange mt-2 text-center font-bold min-h-[20px]">
            {error ? error : ""}
          </p>
          <div className="mt-6">
            <button
              type="button"
              onClick={handleResend}
              className={`w-fit flex mx-auto items-center disabled:text-[#00000080] ${showResendIcon ? "opacity-100" : "opacity-0"}`}
              disabled={isOTPResent}
              aria-label={t("resend")}
            >
              <span className="cursor-pointer">
                <img src="/icons/resend.svg" alt="resend" />
              </span>
              <span className="cursor-pointer ml-1 text-14 md:text-16">
                <span>{t("resend")}</span>
              </span>
            </button>
          </div>
        </div>
        <div className="mt-6 flex items-center">
        {isLoading ? (
          <Loader size={24} aria-label={t("loading")} />
        ) : (
        <button
            type="submit"
            className="btn-green w-full max-w-xs mx-auto py-3 text-base md:text-lg uppercase"
            aria-label={t("verify")}
          >
            {isLoading ? t("verifying") : t("verify")}
          </button>
        )}
        </div>
      </form>
    </div>
  );
};

export default OTPInput;
